import { isPlatformServer } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { AssetColorLabel, AssetDto, AssetStatus, GalleryDto } from '@picflow/types';
import { AssetFeedback } from 'src/app/gallery-shared/interfaces/asset-feedback';
import { copy } from 'src/app/lib/copy';
import { getAllRouteData } from 'src/app/lib/get-all-route-data';
import { getFullPath } from 'src/app/lib/get-full-path';
import { disableBodyScroll } from 'src/app/lib/toggle-body-scroll';
import { AssetFeedbackService } from 'src/app/services/asset-feedback.service';
import { AssetService } from 'src/app/services/asset.service';
import { GalleryService } from 'src/app/services/gallery.service';
import { RedirectService } from 'src/app/services/redirect.service';
import { StorageService } from 'src/app/services/storage.service';
import { ToastService } from 'src/app/services/toast.service';
import { UpdateService } from 'src/app/services/update.service';
import { UploadService } from 'src/app/services/upload.service';
import { AssetTransitionService } from '../services/asset-transition.service';

@Injectable({
  providedIn: 'root',
})
export class AssetPageActivation {
  constructor(
    private assetTransition: AssetTransitionService,
    private assetService: AssetService,
    private galleryService: GalleryService,
    private assetFeedbackService: AssetFeedbackService,
    private redirectService: RedirectService,
    private storage: StorageService,
    private route: ActivatedRoute,
    private updateService: UpdateService,
    private uploadService: UploadService,
    private toasts: ToastService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  private async getAsset(next: ActivatedRouteSnapshot): Promise<{ gallery?: GalleryDto; asset?: AssetDto }> {
    const assetId = next.params.assetId.replace('}', '');
    const fullPath = getFullPath(next);
    const gallery = await this.galleryService.getPublicGalleryFromSnapshot(next, { isPublic: !fullPath.includes('a') });
    if (!gallery) return {};

    let asset: AssetDto;
    try {
      asset = await this.assetService.getAsset(assetId, gallery.id);
      const status = asset.version_data['status'];
      if (status === 'pending') {
        // wait for the asset to be ready
        this.updateService.subscribeGallery(gallery.id);
        const toast = this.toasts.add('Waiting for asset to be processed...', 'info');
        await this.uploadService.waitForAssetProcessing(asset.id);
        this.toasts.close(toast);
      }
    } catch (err) {
      if (!(err instanceof HttpErrorResponse)) throw err;
      console.warn(err);
    }
    if (!asset || asset.gallery !== gallery.id) return { gallery };
    return { asset, gallery };
  }

  async canActivate(next: ActivatedRouteSnapshot): Promise<UrlTree | boolean> {
    const assetId = next.params.assetId.replace('}', '');
    if (isPlatformServer(this.platformId)) return true;
    const { asset, gallery } = await this.getAsset(next);
    const path = getFullPath(next);
    if (!asset) return this.redirectService.redirectTo(path.slice(0, -1));
    if (next.queryParams.feedback) {
      const asset = await this.assetService.getAsset(assetId, gallery.id);

      const feedback: AssetFeedback = JSON.parse(decodeURIComponent(next.queryParams.feedback));
      if (feedback.type === 'like') await this.assetFeedbackService.toggleLike(asset, gallery);
      if (feedback.type === 'status') {
        await this.assetFeedbackService.updateAssetStatus(asset, gallery, <AssetStatus>feedback.content);
      }
      if (feedback.type === 'color_label') {
        await this.assetFeedbackService.updateColorLabel(asset, gallery, <AssetColorLabel>feedback.content);
      }
      if (feedback.type === 'comment') {
        const annotation = this.storage.get('annotation') || [];
        await this.assetFeedbackService.sendComment(asset, gallery, <string>feedback.content, annotation);
      }
      let query = copy(next.queryParams);
      delete query.feedback;

      return this.redirectService.redirectTo(path, query, 'comments');
    }

    // using this.route.snapshot instead of next because otherwise that breaks the page
    const data = getAllRouteData(this.route.snapshot);

    if (data.data?.asset) return true; // going from one asset to another

    disableBodyScroll('asset-page');
    const el = <HTMLElement>(
      (document.querySelector(`#asset-${assetId} img`) || document.querySelector(`#asset-${assetId} video`))
    );
    if (!el) return true;
    const embedMode = path.includes('embed');

    const sideMenuOpen = next.fragment || this.storage.get('asset-sidebar');
    if (!embedMode) await this.assetTransition.transitionToAsset(asset, el, sideMenuOpen);
    return true;
  }
}
